/**
 * useNotebookCreation (Intelligence Studio)
 *
 * SRP hook: create notebook + optional initial parallel uploads.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 */

import { applyVaultStateToNotebook } from '@/pages/intelligence-studio/utils/vaultState';

const STATE_RESOLVER_ENABLED = process.env.REACT_APP_IS_STATE_RESOLVER !== 'false';

export default function useNotebookCreation({
  intelligenceStudioApi,
  uploadLockRef,
  formatBytes,
  clearNanoMetadataRows,
  setNanoMetadataRowsDirectly,
  uploadFilesBatchForNanoMetadata,
  uploadFileForNanoMetadata,
  setNotebooks,
  setSelectedNotebook,
  setShowCreateModal,
  setView,
  setSearchParams,
  setError,
}) {
  const handleCreateNotebook = async (title, files = []) => {
    try {
      if (!Array.isArray(files) || files.length === 0) {
        setError?.('Please add at least one source file to create a studio.');
        return;
      }
      const response = await intelligenceStudioApi.createNotebook(title);

      // ✅ FIX: Robust parsing - apiClient returns {success, data}
      let newNotebook = null;
      if (response?.data?.id) {
        newNotebook = response.data;
      } else if (response?.id) {
        newNotebook = response;
      }

      if (!newNotebook) {
        throw new Error('Failed to create notebook - invalid response');
      }

      console.log(`📓 [Create] New notebook: ${newNotebook.id}, title: ${newNotebook.title}`);

      // ✅ FIX: Add placeholder files with 'uploading' status immediately
      // ✅ ENTERPRISE: Include originalFile for retry capability
      if (files.length > 0) {
        const placeholderFiles = files.map((file, idx) => ({
          id: `temp_${Date.now()}_${idx}`,
          fileId: `temp_${Date.now()}_${idx}`,
          originalName: file.name,
          name: file.name,
          size: file.size,
          status: 'uploading',
          progress: 0,
          originalFile: file, // ✅ CRITICAL: Store original file for retry
        }));
        newNotebook.files = placeholderFiles;
      }

      setNotebooks(prev => [newNotebook, ...prev]);
      setSelectedNotebook(newNotebook);
      setShowCreateModal(false);
      setView('notebook'); // Switch to notebook view

      // Update URL with notebook ID
      setSearchParams({ id: newNotebook.id });

      // ✅ FIXED: Upload files IN PARALLEL with real-time updates
      if (files.length > 0 && newNotebook.id) {
        console.log(`🚀 [Upload] Starting PARALLEL upload of ${files.length} files`);

        // ✅ NANO-METADATA ENGINE: Trigger instant file DNA extraction for ALL files
        // This runs IN PARALLEL with main upload for instant SmartTable display
        console.log(`🔬 [NanoMetadata] Starting parallel DNA extraction for ${files.length} files`);

        // ✅ CRITICAL FIX: Add ALL placeholder rows IMMEDIATELY and SYNCHRONOUSLY
        // This ensures SmartTable appears FIRST, before any async operations
        const allPlaceholders = files.map((file, i) => ({
          file_id: `temp_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`,
          isLoading: true,
          originalFile: file,
          basic_info: {
            name: file.name,
            extension: file.name.split('.').pop()?.toLowerCase() || '',
            size_bytes: file.size,
            size_formatted: formatBytes(file.size),
          },
          quick_health: { ai_readiness_score: 0 },
          context_type_guess: null,
          details: { micro_insight: 'Analyzing...' },
          processing_status: { ocr: 'analyzing', vectorization: 'pending' },
        }));

        // ✅ FIX: Clear existing rows FIRST to prevent duplicates, then add placeholders
        clearNanoMetadataRows();

        // ✅ SYNCHRONOUS STATE UPDATE - Table appears IMMEDIATELY with fresh placeholders
        setNanoMetadataRowsDirectly(allPlaceholders);
        console.log(
          `✅ [NanoMetadata] Set ${allPlaceholders.length} placeholder rows (cleared old rows first)`
        );

        // ✅ ENTERPRISE FIX: Use batch upload for large file sets (>10 files) for better performance
        // Note: uploadFileForNanoMetadata will UPDATE existing placeholders, not add new ones
        // because the hook has deduplication by filename
        if (files.length > 10) {
          console.log(
            `📦 [NanoMetadata] Using BATCH upload for ${files.length} files (faster for large sets)`
          );

          // Chunk files into batches of 20 (backend limit)
          const BATCH_SIZE = 20;
          const chunks = [];
          for (let i = 0; i < files.length; i += BATCH_SIZE) {
            chunks.push(files.slice(i, i + BATCH_SIZE));
          }

          // Upload all chunks in parallel for maximum speed
          // Each batch will replace corresponding placeholder rows
          Promise.all(
            chunks.map((chunk, chunkIndex) => {
              const startIndex = chunkIndex * BATCH_SIZE;
              return uploadFilesBatchForNanoMetadata(
                chunk,
                startIndex,
                allPlaceholders.slice(startIndex, startIndex + chunk.length)
              );
            })
          )
            .then(() => {
              console.log(`✅ [NanoMetadata] All ${chunks.length} batch chunks completed`);
            })
            .catch(err => {
              console.error(`❌ [NanoMetadata] Batch upload error:`, err);
            });
        } else {
          // For smaller sets, use individual uploads (better error handling per file)
          // The hook's deduplication will prevent adding duplicate rows
          files.forEach((file, i) => {
            const placeholder = allPlaceholders[i];
            uploadFileForNanoMetadata(file, placeholder.file_id);
          });
        }

        // ✅ SMART UPLOAD: Pre-check + Upload with intelligent deduplication
        const uploadPromises = files.map(async (file, i) => {
          // ✅ ENTERPRISE: Upload lock to prevent double-uploads
          const fileKey = `${newNotebook.id}:${file.name}:${file.size}`;
          if (uploadLockRef.current.has(fileKey)) {
            console.log(`🔒 [Upload] SKIPPED "${file.name}" - already uploading`);
            return { success: false, skipped: true, index: i };
          }
          uploadLockRef.current.add(fileKey);

          const tempId = `temp_${Date.now()}_${i}`;

          // ✅ STEP 1: Smart Pre-Check (fast, determines action)
          try {
            const preCheck = await intelligenceStudioApi.checkFileBeforeUpload(newNotebook.id, file);

            if (!preCheck.shouldUpload) {
              // ✅ ENTERPRISE UX: Mark as duplicate but KEEP visible (don't remove)
              // User needs to see what happened to their selected file
              console.log(
                `⚠️ [SmartUpload] "${file.name}" is duplicate content - marking as duplicate`
              );
              uploadLockRef.current.delete(fileKey);

              setSelectedNotebook(prev => ({
                ...prev,
                files: prev.files.map((f, idx) =>
                  idx === i
                    ? {
                        ...f,
                        status: 'duplicate',
                        statusMessage: '📎 Already in vault (hash match)',
                        existingFileId: preCheck.existingFileId,
                        cachedQuality: preCheck.cachedQuality,
                        progress: 100,
                        error: null, // Not an error - just info
                      }
                    : f
                ),
              }));

              return { success: true, isDuplicate: true, index: i, preCheck }; // success=true since file exists
            }

            // Update UI based on expected speed
            const statusMessage =
              preCheck.action === 'UPLOAD_FAST'
                ? '⚡ Using cache...'
                : preCheck.action === 'UPLOAD_MEDIUM'
                  ? '📦 Partial cache...'
                  : 'Processing...';

            setSelectedNotebook(prev => ({
              ...prev,
              files: prev.files.map((f, idx) =>
                idx === i ? { ...f, status: 'uploading', progress: 30, statusMessage } : f
              ),
            }));
          } catch (checkErr) {
            console.warn(
              `⚠️ [SmartUpload] Pre-check failed for "${file.name}", proceeding:`,
              checkErr.message
            );
            // Pre-check failed - proceed with upload anyway (fail-open)
            setSelectedNotebook(prev => ({
              ...prev,
              files: prev.files.map((f, idx) =>
                idx === i ? { ...f, status: 'uploading', progress: 30 } : f
              ),
            }));
          }

          // ✅ STEP 2: Actual Upload
          try {
            const startTime = Date.now();
            const uploadResult = await intelligenceStudioApi.uploadFile(newNotebook.id, file);
            const elapsed = Date.now() - startTime;

            // Check if file used cache
            const usedCache = uploadResult?.data?.usedFullCache || uploadResult?.data?.usedCache;

            if (usedCache) {
              console.log(`⚡ [Upload] File ${i + 1} "${file.name}" CACHED! (${elapsed}ms)`);
            } else {
              console.log(`✅ [Upload] File ${i + 1} "${file.name}" processed (${elapsed}ms)`);
            }

            // ✅ Update this specific file to 'processed' immediately
            setSelectedNotebook(prev => {
              const newFile = uploadResult?.data || {};
              return {
                ...prev,
                files: prev.files.map((f, idx) =>
                  idx === i
                    ? {
                        ...f,
                        ...newFile,
                        status: usedCache ? 'restoring' : 'processing',
                        fromFullCache: usedCache,
                      }
                    : f
                ),
              };
            });

            // ✅ Release upload lock on success
            uploadLockRef.current.delete(fileKey);
            return { success: true, index: i, file: uploadResult?.data };
          } catch (err) {
            // ✅ Release upload lock on error
            uploadLockRef.current.delete(fileKey);

            const isDuplicateError =
              err.response?.data?.code === 'DUPLICATE_FILE' || err.response?.status === 409;
            const existingFileId =
              err.response?.data?.existingFileId || err.response?.data?.details?.existingFileId;
            const isWorkerUnavailable =
              err.response?.data?.code === 'WORKERS_UNAVAILABLE' || err.response?.status === 503;
            const isUnsupportedType =
              err.response?.data?.code === 'UNSUPPORTED_FILE_TYPE' || err.response?.status === 415;

            if (isDuplicateError) {
              // ✅ GRACEFUL DUPLICATE HANDLING: Show as "already uploaded", not an error
              console.log(
                `📎 [Upload] File ${i + 1} "${file.name}" already exists (using existing: ${existingFileId})`
              );
            } else {
              console.log(`❌ [Upload] File ${i + 1} "${file.name}" ERROR: ${err.message}`);
            }

            if (isWorkerUnavailable) {
              setError(
                'Workers are offline. Ingestion is temporarily unavailable. Please start workers and retry.'
              );
            } else if (isUnsupportedType) {
              setError('Unsupported file type. Please upload a supported document format.');
            }

            // ✅ ENTERPRISE FIX: Preserve ALL files with their real status (never auto-delete)
            setSelectedNotebook(prev => {
              let updatedFiles;

              if (isDuplicateError) {
                // ✅ ENTERPRISE UX: Mark as duplicate but KEEP visible (don't remove)
                // User needs to see what happened to their selected file
                updatedFiles = prev.files.map((f, idx) => {
                  if (idx !== i) return f;
                  return {
                    ...f,
                    status: 'duplicate',
                    statusMessage: '📎 Already in vault',
                    existingFileId: existingFileId,
                    progress: 100,
                    error: null, // Not an error - just info
                  };
                });
                console.log(
                  `📎 [Upload] Marked "${file.name}" as duplicate (content exists as ${existingFileId})`
                );
              } else {
                // ✅ Real error - mark for retry and KEEP visible
                updatedFiles = prev.files.map((f, idx) => {
                  if (idx !== i) return f;
                  return {
                    ...f,
                    status: isWorkerUnavailable || isUnsupportedType ? 'failed' : 'error',
                    statusMessage: isWorkerUnavailable
                      ? 'Workers offline - ingestion paused'
                      : isUnsupportedType
                        ? 'Unsupported file type'
                        : undefined,
                    error: err.response?.data?.error || err.message || 'Upload failed',
                    errorCode: err.response?.data?.code || undefined,
                    originalFile: f.originalFile || file,
                    progress: 0,
                  };
                });
              }

              return {
                ...prev,
                files: updatedFiles,
              };
            });

            // ✅ Duplicates count as success (file is in vault), real errors don't
            return {
              success: isDuplicateError, // Duplicate = success since file exists
              index: i,
              error: isDuplicateError ? null : err.message,
              isDuplicate: isDuplicateError,
            };
          }
        });

        // Wait for ALL uploads to complete
        const results = await Promise.all(uploadPromises);
        const successful = results.filter(r => r.success).length;
        const duplicates = results.filter(r => r.isDuplicate).length;
        const errors = results.filter(r => !r.success && !r.isDuplicate).length;

        console.log(
          `📊 [Upload] Complete: ${successful} success, ${duplicates} duplicates, ${errors} errors`
        );

        // Final refresh to get complete data
        const updated = await intelligenceStudioApi.getNotebook(newNotebook.id);
        let serverNotebook = updated?.data?.id ? updated.data : updated;

        if (serverNotebook) {
          if (STATE_RESOLVER_ENABLED) {
            try {
              const stateResponse = await intelligenceStudioApi.getNotebookState(newNotebook.id);
              const stateData =
                stateResponse?.data?.vaultState
                  ? stateResponse.data
                  : stateResponse?.data?.data?.vaultState
                    ? stateResponse.data.data
                    : stateResponse?.vaultState
                      ? stateResponse
                      : null;
              if (stateData) {
                serverNotebook = applyVaultStateToNotebook(serverNotebook, stateData);
              }
            } catch (stateErr) {
              console.warn('⚠️ [Upload] Vault state load failed:', stateErr?.message || stateErr);
            }
          }

          setSelectedNotebook(serverNotebook);

          setNotebooks(prev => prev.map(n => (n.id === serverNotebook.id ? serverNotebook : n)));
        }
      }
    } catch (err) {
      console.error('Failed to create notebook:', err);
      setError('Failed to create notebook');
    }
  };

  return { handleCreateNotebook };
}
